/**
 * @description 趋势折线图组件
 * @date 2025-09-26
 * @author lv.chen
 */
import React, { useRef, useMemo, useEffect } from 'react';
import classnames from 'classnames';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, GraphicComponent } from 'echarts/components';
import useMomentLocale from '@home/hooks/useMomentLocale';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { fillWeeklyTrends, getMonthFirstWeekMap } from './utils';

import type { ITrendDataPoint } from './types';

import './index.scss';

import StarMarkSvg2025 from '../../v1/assets/svgs/star-mark.svg';
import StarMarkSvg2026 from '../../v2/assets/svgs/star-mark.svg';

// 注册必要的组件
echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, GraphicComponent, CanvasRenderer]);

interface ITrendLineChartProps extends IBaseProps {
  /**
   * 图表高度
   */
  height?: number;

  /**
   * 是否显示加载状态
   */
  loading?: boolean;

  /**
   * 趋势数据
   */
  trends?: IQuarterReportPVHistory[] | null;

  /**
   * 年份，用于区分不同年份的资源
   */
  year?: number;
}

/**
 * 趋势折线图组件
 */
const TrendLineChart: React.FC<ITrendLineChartProps> = (props) => {
  const { style, className, loading = false, height = 200, trends, year } = props;

  const axisLabelColor = year === 2026 ? '#1E1915' : '#FFFFFF';

  useMomentLocale();

  const { locale } = useIntlExtend();

  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  /**
   * @date 2025-10-05
   * @description 处理数据，填充缺失周的数据，转换为图表需要的格式
   * @author lv.chen
   */
  const chartData = useMemo<ITrendDataPoint[]>(() => {
    if (!trends || trends.length === 0) {
      return [];
    }

    // 使用fillWeeklyTrends填充缺失周的数据
    const filledTrends = fillWeeklyTrends(trends);

    return filledTrends.map((item) => {
      return {
        date: item?.date,
        value: item?.pv,
      };
    });
  }, [trends]);

  /**
   * @date 2025-10-05
   * @description 获取所有周的日期列表（用于X轴刻度）
   * @author lv.chen
   */
  const weekDates = useMemo<string[]>(() => {
    if (chartData.length === 0) return [];

    return chartData.map((trend) => trend?.date);
  }, [chartData]);

  /**
   * @date 2025-10-05
   * @description 计算每个月的第一个周的日期映射表
   * @author lv.chen
   * @returns 月份到第一周日期的映射，格式为 { "2025-01": "2025-01-05", "2025-02": "2025-02-02", ... }
   */
  const monthFirstWeekMap = useMemo<IRecord<string>>(() => {
    return getMonthFirstWeekMap(chartData);
  }, [chartData]);

  /**
   * @date 2025-10-05
   * @description 生成图表配置选项
   * @author lv.chen
   */
  const getChartOptions = useMemo(() => {
    if (chartData.length === 0) return {};

    // 准备数据，对最高点单独设置样式
    const maxValue = Math.max(...chartData.map((item) => item?.value ?? 0));
    let maxMarked = false;
    const seriesData = chartData.map((item) => {
      // 只标记第一个最大值点，避免重复
      if (!maxMarked && item?.value === maxValue && maxValue > 0) {
        maxMarked = true;
        return {
          value: item?.value,
          itemStyle: {
            color: year === 2026 ? '#FD4090' : 'transparent',
            borderColor: year === 2026 ? '#FFFFFF' : 'transparent',
            borderWidth: year === 2026 ? 2 : 0,
          },
          symbolSize: year === 2026 ? 8 : 4,
        };
      }
      return item?.value;
    });

    return {
      grid: {
        show: false, // 隐藏网格线
        left: 10,
        right: 10,
        top: 30,
        bottom: 25,
      },
      tooltip: {
        show: false, // 隐藏tooltip提示
      },
      xAxis: {
        type: 'category',
        data: weekDates,
        axisLine: {
          show: true,
          lineStyle: {
            width: 0.3,
            color: '#CBDBF2',
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          interval: 0, // 强制显示所有刻度
          color: axisLabelColor,
          fontSize: 12,
          margin: 10,
          fontWeight: 'normal',
          formatter: (date: string) => {
            // 提取当前日期的月份标识（YYYY-MM格式）
            const monthKey = formatDateStr(date, 'YYYY-MM');

            // 从映射表中获取该月的第一周日期
            const firstWeekDate = monthFirstWeekMap[monthKey];

            // 判断当前日期是否是该月的第一周
            if (date === firstWeekDate) {
              // 是第一周，显示月份标签
              return formatDateStr(date, 'MMM');
            }

            // 不是第一周，不显示标签
            return '';
          },
        },
        splitLine: {
          show: false, // 隐藏网格线
        },
      },
      yAxis: {
        type: 'value',
        show: false, // 隐藏Y轴
        splitLine: {
          show: false, // 隐藏网格线
        },
      },
      series: [
        {
          type: 'line',
          data: seriesData,
          smooth: false,
          clip: false,
          lineStyle: {
            color: '#0575F2',
            width: 1,
          },
          itemStyle: {
            borderColor: '#FBF3F0',
            borderWidth: 1,
          },
          symbol: 'circle',
          symbolSize: 4,
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: '#0575F2', // 0% 透明
                },
                {
                  offset: 1,
                  color: '#F7EDE500', // 100% 半透明
                },
              ],
            },
          },
          emphasis: {
            itemStyle: {
              borderColor: '#ffffff',
              borderWidth: 3,
              shadowColor: 'rgba(255, 255, 255, 0.5)',
              shadowBlur: 10,
            },
          },
          markPoint: {
            data: [
              {
                type: 'max',
                name: '最大值',
              },
            ],
            symbol: `image://${year === 2026 ? StarMarkSvg2026 : StarMarkSvg2025}`,
            symbolSize: [30, 22],
            symbolOffset: [0, -15],
            itemStyle: {
              color: 'transparent',
              borderWidth: 0,
            },
            label: {
              show: false,
            },
            emphasis: {
              disabled: true,
            },
          },
        },
      ],
    };
  }, [chartData, weekDates, monthFirstWeekMap, axisLabelColor, year]);

  // 初始化图表
  useEffect(() => {
    if (!chartRef.current) return;

    // 销毁已存在的实例
    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    // 创建新实例
    chartInstance.current = echarts.init(chartRef.current);

    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  // 更新图表配置
  useEffect(() => {
    if (!chartInstance.current || loading || chartData.length === 0) return;

    chartInstance.current.setOption(getChartOptions, true);
  }, [locale, getChartOptions, loading, chartData]);

  // 渲染内容
  const renderContent = () => {
    if (loading) {
      // TODO: 完善折线图数据加载交互
      return (
        <div className="trend-line-chart__loading" style={{ height }}>
          加载中...
        </div>
      );
    }

    if (chartData.length === 0) {
      // TODO: 完善折线图无数据交互
      return (
        <div className="trend-line-chart__empty" style={{ height }}>
          暂无数据
        </div>
      );
    }

    return <div ref={chartRef} className="trend-line-chart__container" style={{ height }} />;
  };

  return (
    <div style={style} className={classnames('trend-line-chart', className)}>
      {renderContent()}
    </div>
  );
};

export default TrendLineChart;
